import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  TablePagination,
  IconButton,
} from "@mui/material";
import { EmojiEvents } from "@mui/icons-material";
import { IListSubmissionTcc } from "@/api";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface Contestant {
  id: number;
  name: string;
  image: string;
}

interface ScoringCriterion {
  id: string;
  name: string;
  maxScore: number;
}

interface ResultsSummaryProps {
  contestants: IListSubmissionTcc["data"]["data"];
  criteria: ScoringCriterion[];
  scores: Record<number, Record<string, number>>;
  onScoreAnother: () => void;
}

export default function ResultsSummary({
  contestants,
  criteria,
  scores,
  onScoreAnother,
}: ResultsSummaryProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expanded, setExpanded] = React.useState({});

  const toggleExpand = (index: any) => {
    setExpanded((prev: any) => ({ ...prev, [index]: !prev[index] }));
  };
  // Sort contestants by their score (highest first)
  const rankedContestants = [...contestants].sort(
    (a, b) => (b.score ?? 0) - (a.score ?? 0)
  );

  // const allJudges = Array.from(
  //   new Set(contestants.flatMap((submission) => submission.judges.map((j) => j.judge)))
  // );

  // Maximum possible score
  // const maxPossibleScore = criteria.reduce(
  //   (sum, criterion) => sum + criterion.maxScore,
  //   0
  // );
  const maxPossibleScore = process.env.MAX_SCORE;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new(); // Tạo workbook mới

    const wsData = [
      ["Rank", "Contestant", "AI Score", "AI Score Details", "Judge", "Score"], // Tiêu đề cột
      ...rankedContestants.map((contestant, index) => {
        let aiScoreDetails = "N/A";

        // Kiểm tra nếu dữ liệu JSON hợp lệ thì xử lý
        try {
          const aiData = JSON.parse(
            // contestant.ai_score_data.replace(/\\"/g, '"')
            contestant.ai_score_data.replace(/\\+/g, "")
          );
          const scores = aiData.scores
            ? Object.entries(aiData.scores)
                .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
                .join("\n")
            : "No scores available";

          const feedback = aiData.feedback
            ? Object.entries(aiData.feedback)
                .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
                .join("\n")
            : "No feedback available";

          aiScoreDetails = `Scores:\n${scores}\n\nFeedback:\n${feedback}\n\nSummary:\n${
            aiData.summary || "N/A"
          }`;
        } catch (error) {
          console.error("Error parsing AI score data:", error);
        }

        return [
          index + 1, // Rank
          contestant.display_name, // Contestant
          contestant.ai_score, // AI Score
          aiScoreDetails, // AI Score Details (phân tích từ JSON)
          contestant.judgeInfo.display_name, // Judge
          contestant.score ?? 0, // Tổng điểm của Judge
        ];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData); // Chuyển dữ liệu thành sheet
    ws["!cols"] = [
      { wch: 10 }, // Rank
      { wch: 20 }, // Contestant
      { wch: 10 }, // AI Score
      { wch: 40 }, // AI Score Details (tăng chiều rộng cột)
      { wch: 20 }, // Judge
      { wch: 10 }, // Score
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Results"); // Thêm sheet vào workbook

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "scoring_results.xlsx"); // Lưu file Excel
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Scoring Results
      </Typography>

      {rankedContestants.length > 0 ? (
        <>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {rankedContestants.slice(0, 3).map((ranked, index) => {
              const contestant = contestants.find(
                (c) => c.user === ranked.user
              );
              if (!contestant) return null;

              return (
                <Grid item xs={12} sm={4} key={contestant.user}>
                  <Card
                    sx={{
                      height: "100%",
                      bgcolor:
                        index === 0 ? "rgba(255, 215, 0, 0.1)" : "inherit",
                      border: index === 0 ? "2px solid gold" : "inherit",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      {index === 0 ? (
                        <EmojiEvents
                          sx={{ fontSize: 40, color: "gold", mb: 1 }}
                        />
                      ) : index === 1 ? (
                        <EmojiEvents
                          sx={{ fontSize: 40, color: "silver", mb: 1 }}
                        />
                      ) : (
                        <EmojiEvents
                          sx={{ fontSize: 40, color: "#cd7f32", mb: 1 }}
                        /> // Bronze color
                      )}
                      <Typography variant="h6" gutterBottom>
                        {index === 0
                          ? "1st Place"
                          : index === 1
                          ? "2nd Place"
                          : "3rd Place"}
                      </Typography>
                      <Typography variant="h5" component="div">
                        {contestant.display_name}
                      </Typography>
                      <Typography variant="h4" color="primary" sx={{ mt: 2 }}>
                        {contestant.score ?? 0} / {maxPossibleScore}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(
                          ((contestant.score ?? 0) / maxPossibleScore) * 100
                        )}
                        % of maximum score
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Rank</b>
                  </TableCell>
                  <TableCell>
                    <b>Contestant</b>
                  </TableCell>
                  {/* {allJudges.map((judge) => (
                    <TableCell key={judge} align="center">{judge} (Judge)</TableCell>
                  ))} */}
                  <TableCell>
                    <b>AI Score</b>
                  </TableCell>
                  <TableCell>
                    <b>AI Score Details</b>
                  </TableCell>
                  <TableCell>
                    <b>Judge</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Score</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rankedContestants
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((contestant, index) => (
                    <>
                      <TableRow
                        key={contestant.user}
                        sx={{
                          bgcolor:
                            index === 0 ? "rgba(255, 215, 0, 0.1)" : "inherit",
                          "&:nth-of-type(odd)": {
                            bgcolor: (theme) => theme.palette.action.hover,
                          },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {index + 1}
                        </TableCell>
                        <TableCell>{contestant.display_name}</TableCell>
                        {/* {allJudges.map((judge) => {
                      const judgeScore = contestant.judges.find((j) => j.judge === judge)?.score ?? "N/A";
                      return <TableCell key={judge} align="center">{judgeScore}</TableCell>;
                    })} */}
                        <TableCell>{contestant.ai_score}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => toggleExpand(index)}>
                            {expanded[index] ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {contestant.judgeInfo?.display_name || "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {contestant.score ?? 0} / {maxPossibleScore}
                        </TableCell>
                      </TableRow>
                      {expanded[index] && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="subtitle1">
                              <b>AI Score Breakdown:</b>
                            </Typography>
                            <ul>
                              {Object.entries(
                                JSON.parse(
                                  contestant.ai_score_data.replace(/\\+/g, "")
                                ).scores
                              ).map(([key, value]) => (
                                <li key={key}>
                                  <b>{key.replace(/_/g, " ")}:</b> {value}
                                </li>
                              ))}
                            </ul>
                            <Typography variant="subtitle1">
                              <b>Feedback:</b>
                            </Typography>
                            <ul>
                              {Object.entries(
                                JSON.parse(
                                  contestant.ai_score_data.replace(/\\+/g, "")
                                ).feedback
                              ).map(([key, value]) => (
                                <li key={key}>
                                  <b>{key.replace(/_/g, " ")}:</b> {value}
                                </li>
                              ))}
                            </ul>
                            <Typography variant="body1">
                              <b>Summary:</b>{" "}
                              {
                                JSON.parse(
                                  contestant.ai_score_data.replace(/\\+/g, "")
                                ).summary
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={exportToExcel}
              startIcon={<EmojiEvents />}
            >
              Export to Excel
            </Button>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rankedContestants.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </>
      ) : (
        <Typography variant="body1" sx={{ my: 4 }}>
          No scores have been submitted yet.
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onScoreAnother}
          size="large"
        >
          Score Another Contestant
        </Button>
      </Box>
    </Box>
  );
}
